import { ReactNode, useRef } from "react";
import styled, { css } from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutlineAtEdge } from "@pages/AdminDashboard/theme/focusOutline";
import { VisuallyHidden } from "@pages/AdminDashboard/ui/DataTable/visuallyHidden";

const { bannerTone, color, density, fieldTone, radius, typeScale } = adminTokens;
const [, , spaceMd] = adminTokens.spacing;
const labelGap = 6;
const iconSize = 14;
// On a phone a field is 48px with 12px corners and 16px text, so iOS does not zoom
// into it, and it spans its container whatever width it is given
// (PHONE-R31…R33).
const phoneControlHeight = 48;
const phoneTextSize = "16px";

let fieldCount = 0;

// A stable id for a label and the message that describes its control.
export const useFieldId = (): string => {
  const idRef = useRef<string>();
  if (!idRef.current) {
    fieldCount += 1;
    idRef.current = `admin-field-${fieldCount}`;
  }

  return idRef.current;
};

interface ControlStyleProps {
  $width?: number;
  $isPhone?: boolean;
}

// The look every text-like control shares: 36px, 8px corners, a 1px border, and
// the focus, invalid and disabled looks (FIELD-R2, R5, R6, R9).
export const controlStyle = css<ControlStyleProps>`
  box-sizing: border-box;
  width: ${({ $width, $isPhone }) => ($width && !$isPhone ? `${$width}px` : "100%")};
  max-width: 100%;
  height: ${({ $isPhone }) => ($isPhone ? phoneControlHeight : density.controlHeight)}px;
  padding: 0 ${spaceMd}px;
  border: 1px solid ${color.border};
  border-radius: ${({ $isPhone }) => ($isPhone ? radius.row : radius.input)}px;
  background-color: ${color.surface};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${({ $isPhone }) => ($isPhone ? phoneTextSize : typeScale.body.fontSize)};

  &::placeholder {
    color: ${color.inkSoft};
    opacity: 1;
  }

  /* Tab also stops on a date's calendar button, inside the field, where the field
     itself no longer matches :focus; the field keeps the outline while anything in it
     has the focus. */
  &:focus-visible,
  &[type="date"]:focus-within {
    ${focusOutlineAtEdge}
  }

  &[aria-invalid="true"] {
    background-color: ${bannerTone.error.background};
    border-color: ${bannerTone.error.border};
  }

  &:disabled {
    background-color: ${color.warmGray};
    color: ${color.disabledText};
    cursor: not-allowed;
  }
`;

const Frame = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${labelGap}px;
  min-width: 0;
`;

const Label = styled.label`
  color: ${color.navy};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const MessageRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${labelGap}px;
`;

const MessageText = styled.span`
  color: ${bannerTone.error.text};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

interface ErrorMessageProps {
  id: string;
  // "alert" for a message that appears after the admin has changed something.
  role?: "alert";
  children: ReactNode;
}

// The message a field gives below its control (FIELD-R6).
export const ErrorMessage = ({ id, role, children }: ErrorMessageProps) => (
  <MessageRow id={id} role={role}>
    <svg
      aria-hidden="true"
      width={iconSize}
      height={iconSize}
      viewBox="0 0 24 24"
      fill="none"
      stroke={fieldTone.errorIcon}
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </svg>
    <MessageText>{children}</MessageText>
  </MessageRow>
);

interface FieldFrameProps {
  label: string;
  controlId: string;
  errorId: string;
  error?: string;
  isLabelHidden?: boolean;
  children: ReactNode;
}

// A label above a control and, when given, an error below it (FIELD-R1, R6).
// A hidden label still names the control and takes no room (FIELD-R14).
export const FieldFrame = ({
  label,
  controlId,
  errorId,
  error,
  isLabelHidden = false,
  children,
}: FieldFrameProps) => (
  <Frame>
    {isLabelHidden ? (
      <VisuallyHidden as="label" htmlFor={controlId}>
        {label}
      </VisuallyHidden>
    ) : (
      <Label htmlFor={controlId}>{label}</Label>
    )}
    {children}
    {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
  </Frame>
);
